import type { Distribution, GroupLayoutSpec, Id } from '../types.js';
import type { Rng } from '../rng.js';

/** Gruppen-Layout (docs/04 §2): Größe *oder* Anzahl vorgeben */
export interface GroupLayout {
  count: number;
  sizes: number[];
  /** z. B. "6 Gruppen: 5× 4 Teams, 1× 3 Teams" */
  summary: string;
  uneven: boolean;
}

export function groupLayout(n: number, spec: GroupLayoutSpec): GroupLayout {
  if (n <= 0) return { count: 0, sizes: [], summary: 'keine Teams', uneven: false };
  const count = 'groupSize' in spec ? Math.max(1, Math.ceil(n / Math.max(1, spec.groupSize))) : Math.max(1, Math.min(n, spec.groupCount));
  const base = Math.floor(n / count);
  const rest = n % count;
  const sizes = Array.from({ length: count }, (_, i) => base + (i < rest ? 1 : 0));
  const buckets = new Map<number, number>();
  for (const s of sizes) buckets.set(s, (buckets.get(s) ?? 0) + 1);
  const parts = [...buckets.entries()].sort((a, b) => b[0] - a[0]).map(([size, c]) => `${c}× ${size} Teams`);
  return { count, sizes, summary: `${count} Gruppe${count === 1 ? '' : 'n'}: ${parts.join(', ')}`, uneven: rest !== 0 };
}

/**
 * Verteilt eine Rangliste (1 = stärkstes) auf Gruppen.
 * snake: 1 2 3 4 / 8 7 6 5 / 9 10 11 12 …
 * pots:  Topf k = Ränge k·G+1 … (k+1)·G, innerhalb des Topfes zufällig
 * random: alles zufällig
 */
export function distribute(ranked: readonly Id[], layout: GroupLayout, method: Distribution, rng: Rng): Id[][] {
  const G = layout.count;
  const groups: Id[][] = Array.from({ length: G }, () => []);
  const order = method === 'random' ? rng.shuffle(ranked) : ranked.slice();
  let idx = 0;
  let row = 0;
  while (idx < order.length) {
    let slice = order.slice(idx, idx + G);
    if (method === 'pots') slice = rng.shuffle(slice);
    const cols = method === 'snake' && row % 2 === 1 ? [...slice.keys()].reverse() : [...slice.keys()];
    for (const [k, gi] of cols.entries()) {
      const team = slice[k];
      if (team !== undefined) (groups[gi] as Id[]).push(team);
    }
    idx += G;
    row++;
  }
  // Gruppengrößen an Layout anpassen (letzte Zeile ist unvollständig — die
  // ersten `rest` Gruppen sollen das Extra-Team haben)
  return rebalance(groups, layout.sizes);
}

function rebalance(groups: Id[][], sizes: number[]): Id[][] {
  const out = groups.map((g) => g.slice());
  for (let pass = 0; pass < out.length * 2; pass++) {
    const over = out.findIndex((g, i) => g.length > (sizes[i] as number));
    const under = out.findIndex((g, i) => g.length < (sizes[i] as number));
    if (over < 0 || under < 0) break;
    const moved = (out[over] as Id[]).pop() as Id;
    (out[under] as Id[]).push(moved);
  }
  return out;
}

export interface Violation {
  groupIndex: number;
  teamIds: [Id, Id];
  reason: 'sameClub' | 'keepApart';
}

export interface ConstraintSpec {
  clubOf: (id: Id) => string | undefined;
  avoidSameClub: boolean;
  keepApart: readonly [Id, Id][];
}

export function findViolations(groups: readonly (readonly Id[])[], spec: ConstraintSpec): Violation[] {
  const apart = new Set(spec.keepApart.map(([a, b]) => (a < b ? `${a}|${b}` : `${b}|${a}`)));
  const out: Violation[] = [];
  groups.forEach((g, gi) => {
    for (let i = 0; i < g.length; i++) {
      for (let j = i + 1; j < g.length; j++) {
        const a = g[i] as Id;
        const b = g[j] as Id;
        const key = a < b ? `${a}|${b}` : `${b}|${a}`;
        if (apart.has(key)) out.push({ groupIndex: gi, teamIds: [a, b], reason: 'keepApart' });
        else if (spec.avoidSameClub) {
          const ca = spec.clubOf(a);
          if (ca && ca === spec.clubOf(b)) out.push({ groupIndex: gi, teamIds: [a, b], reason: 'sameClub' });
        }
      }
    }
  });
  return out;
}

/**
 * Nebenbedingungen reparieren (docs/04 §2): Tausche paarweise Teams mit
 * gleicher Stärke-Zeile (gleicher Index in der Gruppe) zwischen Gruppen, solange
 * die Verstöße sinken. Feste Iterationszahl, deterministisch. Restverstöße
 * werden zurückgegeben, nicht verschwiegen.
 */
export function repairConstraints(groups: Id[][], spec: ConstraintSpec, rng: Rng, maxIter = 300): { groups: Id[][]; violations: Violation[] } {
  const cur = groups.map((g) => g.slice());
  let viol = findViolations(cur, spec);
  for (let iter = 0; iter < maxIter && viol.length > 0; iter++) {
    const v = viol[rng.int(viol.length)] as Violation;
    const gi = v.groupIndex;
    const g = cur[gi] as Id[];
    const team = v.teamIds[rng.int(2)] as Id;
    const row = g.indexOf(team);
    // Kandidaten: nur gleiche Stärke-Zeile in anderen Gruppen — so bleiben
    // Setzung (Zeile 0 = Topseeds) und Stärkesumme je Gruppe erhalten
    const candidates: [number, number][] = [];
    cur.forEach((og, ogi) => {
      if (ogi === gi) return;
      if (row < og.length) candidates.push([ogi, row]);
    });
    let improved = false;
    for (const [ogi, k] of candidates) {
      const og = cur[ogi] as Id[];
      const other = og[k] as Id;
      g[row] = other;
      og[k] = team;
      const next = findViolations(cur, spec);
      if (next.length < viol.length) {
        viol = next;
        improved = true;
        break;
      }
      g[row] = team;
      og[k] = other;
    }
    if (!improved) {
      // Sackgasse: einen zufälligen Tausch akzeptieren, um aus lokalen Minima
      // zu kommen (nur wenn nicht schlechter)
      if (candidates.length === 0) break;
      const [ogi, k] = candidates[rng.int(candidates.length)] as [number, number];
      const og = cur[ogi] as Id[];
      const other = og[k] as Id;
      g[row] = other;
      og[k] = team;
      const next = findViolations(cur, spec);
      if (next.length > viol.length) {
        g[row] = team;
        og[k] = other;
      } else viol = next;
    }
  }
  return { groups: cur, violations: viol };
}
